<script setup>
  import { ref, reactive, computed } from 'vue';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useDebounceFn, useMediaQuery } from '@vueuse/core';
  import ToolbarHeaderActionListBase from '@/components/ToolbarHeaderActionListBase.vue';
  import ToolbarHeaderActionMenuPopup from '@/components/ToolbarHeaderActionMenuPopup.vue';
  import DataContainerLoader from '@/components/DataContainerLoader.vue';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ModalWindAlertManage from '@/components/ModalWindAlertManage.vue';
  import ModalAlertDevicesManage from '@/components/ModalAlertDevicesManage.vue';
  import ModalWindAlertHistory from '@/components/ModalWindAlertHistory.vue';
  import AlertSpotSection from '@/components/AlertSpotSection.vue';
  import AlertsPageAccountNeeded from '@/components/AlertsPageAccountNeeded.vue';
  import Icon from '@/components/icons/Icon.vue';

  const windAlertStore = useWindAlertStore();
  const userStore = useUserStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  // Loading bootstrap
  const isLoading = ref(true);

  // Toggle-alert state
  const togglingAlerts = reactive(new Set());

  // Manage-alert modal state
  const showManageModal = ref(false);
  const manageModalAlert = ref(null);
  const manageModalSpotId = ref(null);
  const manageModalSpotName = ref('');

  // Devices modal state
  const showDevicesModal = ref(false);

  // History modal state
  const showHistoryModal = ref(false);

  if (userStore.memberLevel > -1) {
    await windAlertStore.getAlertDevices();
    await windAlertStore.getWindAlerts();
  }
  isLoading.value = false;

  const spots = computed(() => Object.keys(windAlertStore.alertsSortedBySpot));

  const sendAlertStatus = useDebounceFn(async (alert) => {
    try {
      await windAlertStore.changeAlertStatus(alert.id, alert.enabled);
    } catch (err) {
      alert.enabled = !alert.enabled;
    } finally {
      togglingAlerts.delete(alert.id);
    }
  }, 500);

  // Toggle-alert handlers
  const toggleAlert = (alert) => {
    togglingAlerts.add(alert.id);
    sendAlertStatus(alert);
  };

  const deleteAlert = (alert) => windAlertStore.deleteAlert(alert.id);

  // Manage-alert modal handlers
  const openAddAlertModal = (spotId, spotName = '') => {
    manageModalAlert.value = null;
    manageModalSpotId.value = spotId;
    manageModalSpotName.value = spotName;
    showManageModal.value = true;
  };

  const openEditAlertModal = (alert) => {
    manageModalAlert.value = alert;
    manageModalSpotId.value = alert.spot_id;
    manageModalSpotName.value = alert.spot_name || '';
    showManageModal.value = true;
  };

  const handleManageModalClose = () => {
    showManageModal.value = false;
    manageModalAlert.value = null;
    manageModalSpotId.value = null;
    manageModalSpotName.value = '';
  };

  // Devices modal handlers
  const openDevicesModal = () => {
    showDevicesModal.value = true;
  };

  const handleDevicesModalClose = () => {
    showDevicesModal.value = false;
  };

  // History modal handlers
  const openHistoryModal = () => {
    showHistoryModal.value = true;
  };

  const handleHistoryModalClose = () => {
    showHistoryModal.value = false;
  };
</script>

<template>
  <AlertsPageAccountNeeded v-if="userStore.memberLevel === -1" />
  <div v-else class="jwx-page jwx-h-100">
    <div class="jwx-page__container jwx-h-100" data-section-gap="true">
      <header class="jwx-toolbar-header">
        <div class="jwx-toolbar-header__main-content">
          <div class="jwx-toolbar-header__title">
            <h2 class="jwx-toolbar-header__title-text">Alerts</h2>
          </div>
          <ToolbarHeaderActionListBase v-if="isDesktop">
            <template #default="{ classes }">
              <li :class="classes.item">
                <button
                  @click="openDevicesModal"
                  type="button"
                  :class="classes.button"
                >
                  <Icon name="iphone" :class="classes.icon" />
                  <span :class="classes.label">Devices</span>
                </button>
              </li>
              <li :class="classes.item">
                <button
                  @click="openHistoryModal"
                  type="button"
                  :class="classes.button"
                >
                  <Icon name="calendar-days" :class="classes.icon" />
                  <span :class="classes.label">History</span>
                </button>
              </li>
            </template>
          </ToolbarHeaderActionListBase>

          <ToolbarHeaderActionMenuPopup v-else>
            <template #menu-content>
              <ToolbarHeaderActionListBase>
                <template #default="{ classes }">
                  <li :class="classes.item">
                    <button
                      @click="openDevicesModal"
                      type="button"
                      :class="classes.button"
                    >
                      <Icon name="iphone" :class="classes.icon" />
                      <span :class="classes.label">Devices</span>
                    </button>
                  </li>
                  <li :class="classes.item">
                    <button
                      @click="openHistoryModal"
                      type="button"
                      :class="classes.button"
                    >
                      <Icon name="info" :class="classes.icon" />
                      <span :class="classes.label">History</span>
                    </button>
                  </li>
                </template>
              </ToolbarHeaderActionListBase>
            </template>
          </ToolbarHeaderActionMenuPopup>
        </div>
      </header>

      <div v-if="isLoading" class="jwx-spot-list-loader">
        <DataContainerLoader />
      </div>

      <AlertSpotSection
        v-for="spot in spots"
        :key="spot"
        :spotName="spot"
        :alerts="windAlertStore.alertsSortedBySpot[spot]"
        :togglingAlerts="togglingAlerts"
        @addAlert="openAddAlertModal"
        @editAlert="openEditAlertModal"
        @deleteAlert="deleteAlert"
        @toggleAlert="toggleAlert"
      />

      <div v-if="!isLoading && spots.length === 0"  class="jwx-grid jwx-place-content-center jwx-h-100">
        <p class="jwx-text jwx-font-size-lg jwx-text-center jwx-p-jumbo" style="background-color: var(--jwx-surface-level-1); border-radius: 100px;">
          You have no wind alerts configured.
        </p>
      </div>      
    </div>
  </div>
  <ModalWindAlertManage
    :show="showManageModal"
    :alert="manageModalAlert"
    :spotId="manageModalSpotId"
    :spotName="manageModalSpotName"
    @close="handleManageModalClose"
  />
  <ModalAlertDevicesManage
    :show="showDevicesModal"
    @close="handleDevicesModalClose"
  />
  <ModalWindAlertHistory
    :show="showHistoryModal"
    @close="handleHistoryModalClose"
  />
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense>
</template>
